import React, { useState, useEffect, useRef } from "react";
import * as Tone from "tone";
import { getSampleById, getSampleTypeById } from './config/drumSamples';
import { useLanguage } from './context/LanguageProvider';
import { useThemeContext } from './context/ThemeProvider';
import { AppManager } from './config/app';
import { getAllDrumPatterns } from './config/drumPatterns';
import { devLog } from './utils/devLog';
import { getInstrumentConfigById } from './utils/smartInstrumentId';
import { getSafeAudioUrl } from './utils/audioUtils';
import DrumsetEditorPanel from './components/DrumsetEditorPanel';
import PatternSelector from './components/PatternSelector';
import PlaybackControls from './components/PlaybackControls';
import HelpSection from './components/HelpSection';
import DrumGrid from './components/DrumGrid';
import DrumRNNPanel from './components/DrumRNNPanel';
import AsciiOutput from './components/AsciiOutput';

import usePlayback from './hooks/usePlayback';
import usePatternIO from './hooks/usePatternIO';
import { useIOSAudioState } from './hooks/useIOSAudioState';
import { calcStepsPerBar } from './utils/steps';
import { TIME_SIGNATURES } from './config/timeSignatures';
import { DEFAULT_INSTRUMENTS } from './config/defaultInstruments';
import { getDrumKit } from './config/drumKits';
import type { 
  DrumInstrument, 
  DrumGridData, 
  TimeSignature, 
  NoteDivision, 
  DrumsetConfig,
  GrooveType,
  DemoStatus,
  DynamicMessage,
  AudioLoadingStatus
} from './types';

// 打擊強度
const HIT_LEVELS = [null, "x", "X", "g", "f"];

// 詳細定義見 config/timeSignatures.js

// Tempo驗證
const TEMPO_MIN = 10;
const TEMPO_MAX = 600;

// Fix grid length to match stepsPerBar
const fixGridLength = (grid: DrumGridData, stepsPerBar: number): DrumGridData => {
  const newGrid: DrumGridData = {};
  Object.keys(grid).forEach(id => {
    const arr = grid[id] || [];
    if (arr.length > stepsPerBar) {
      newGrid[id] = arr.slice(0, stepsPerBar);
    } else if (arr.length < stepsPerBar) {
      newGrid[id] = [...arr, ...Array(stepsPerBar - arr.length).fill(null)];
    } else {
      newGrid[id] = arr;
    }
  });
  return newGrid;
};

// 驗證並調整Tempo的function 
function validateTempo(value: number | string): number {
  const num = parseInt(value.toString());
  if (isNaN(num) || num <= 0) return TEMPO_MIN;
  if (num > TEMPO_MAX) return TEMPO_MAX;
  return num;
}

// 計算拍子分割
function getBarDivisions(timeSignature: TimeSignature, stepsPerBar: number): number {
  const { beats } = TIME_SIGNATURES[timeSignature];
  const stepsPerBeat = stepsPerBar / beats;
  return stepsPerBeat;
}

interface AsciiDrumEditorProps {
  drumsetConfig?: DrumsetConfig | null;
  onToggleDrumsetEditor: () => void;
  onDrumsetUpdate?: (config: DrumsetConfig) => void;
  showDrumsetEditor: boolean;
  onCloseDrumsetEditor: () => void;
}

export default function AsciiDrumEditor({ 
  drumsetConfig, 
  onToggleDrumsetEditor, 
  onDrumsetUpdate, 
  showDrumsetEditor, 
  onCloseDrumsetEditor 
}: AsciiDrumEditorProps) {
  // 預設樂器陣列
  const [instruments, setInstruments] = useState<DrumInstrument[]>(DEFAULT_INSTRUMENTS);

  // 節拍
  const [timeSignature, setTimeSignature] = useState<TimeSignature>("4/4");
  const [noteDivision, setNoteDivision] = useState<NoteDivision>("16n");
  const [loopCount, setLoopCount] = useState<number>(0); // 無限循環次數
  const [stepsPerBar, setStepsPerBar] = useState<number>(calcStepsPerBar("4/4", "16n"));
  const [tempo, setTempo] = useState<number>(120);

  // 介面
  const { language, changeLanguage, t } = useLanguage();
  const { theme, changeTheme, getClass } = useThemeContext();
  const [advancedMode, setAdvancedMode] = useState<boolean>(false);
  const [debugMode, setDebugMode] = useState<boolean>(process.env.NODE_ENV === 'development'); // 開發環境默認開啟debug
  const [grooveType, setGrooveType] = useState<GrooveType>('normal'); // normal, shuffle, swing
  const [shuffleAmount, setShuffleAmount] = useState<number>(0.8); // 0.0 - 1.0
  const [swingRatio, setSwingRatio] = useState<number>(0.8); // 0.5 - 0.8 (標準swing ratio)
  const [selectedPattern, setSelectedPattern] = useState<string>(""); // 當前選擇的預設pattern
  const [isGridModified, setIsGridModified] = useState<boolean>(false); // 追蹤grid是否被用戶修改
  const [temperature, setTemperature] = useState<number>(1.1); // DrumRNN溫度參數（默認1.1，UI顯示為0.1）
  
  // iOS音頻狀態管理
  const { 
    iosAudioUnlocked, 
    userHasEverUnlocked, 
    shouldShowUnlockReminder,
    setIosAudioUnlocked,
    recordUnlockAttempt,
    checkAudioContextState,
    getUnlockReminderMessage
  } = useIOSAudioState();
  const [audioLoadingStatus, setAudioLoadingStatus] = useState<AudioLoadingStatus>({});
  const [isAudioLoading, setIsAudioLoading] = useState<boolean>(false);
  
  // Demo模式狀態
  const [isDemoMode, setIsDemoMode] = useState<boolean>(false);

  // Demo 狀態: idle, loading, playing, stopping
  const [demoStatus, setDemoStatus] = useState<DemoStatus>('idle');

  // 動態消息顯示狀態
  const [dynamicMessage, setDynamicMessage] = useState<DynamicMessage | null>(null);

  const showDynamicMessage = (message: string, type: "info" | "success" | "warning" | "error" = "info", duration: number = 3000): void => {
    setDynamicMessage({ message, type });
    setTimeout(() => {
      setDynamicMessage(null);
    }, duration);
  };

  // 鼓點網格
  const [grid, setGrid] = useState<DrumGridData>(() => {
    const initial: DrumGridData = {};
    // 使用預設樂器初始化，之後會更新
    DEFAULT_INSTRUMENTS.forEach(({ id }) => {
      initial[id] = Array(64).fill(null);
    });
    return initial;
  });

  // 播放狀態
  const [players, setPlayers] = useState<Record<string, any>>({});
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0);

  // 檔案匯入參考
  const fileInputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
  
  // 追蹤上一次的drumsetConfig，避免不必要的更新
  const lastDrumsetConfigRef = useRef<DrumsetConfig | null>(null);
  
  // 追蹤是否是匯入操作 - 改用state以觸發重新渲染
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const isImportingRef = useRef<boolean>(false);
  
  // 同步ref和state
  useEffect(() => {
    isImportingRef.current = isImporting;
  }, [isImporting]);
  
  // iOS音頻解鎖用的refs
  const iosAudioContextRef = useRef<AudioContext | null>(null);

  // 使用新的Hook系統 - 來自context的t與主題函數
  const { togglePlayback: originalTogglePlayback, stopPlayback: originalStopPlayback, resetStep } = usePlayback({
    isPlaying,
    setIsPlaying,
    isPaused,
    setIsPaused,
    step,
    setStep,
    grid,
    instruments,
    players,
    tempo,
    noteDivision,
    timeSignature,
    stepsPerBar,
    loopCount,
    iosAudioUnlocked,
    showDynamicMessage,
    t,
    setIsDemoMode,
    setIsAudioLoading,
    grooveType,
    shuffleAmount,
    swingRatio
  });

  // 包裝playback函數以確保重置importing狀態
  const togglePlayback = async (): Promise<void> => {
    // 當用戶主動點擊播放時，重置importing狀態
    setIsImporting(false);
    return await originalTogglePlayback();
  };

  const stopPlayback = (): void => {
    // 當用戶主動停止播放時，重置importing狀態
    setIsImporting(false);
    
    // 如果正在Demo模式，也要停止Demo
    if (isDemoMode) {
      setIsDemoMode(false);
      setDemoStatus('idle');
    }
    
    return originalStopPlayback();
  };

  const { exportPattern, importPattern, loadPresetPattern, triggerImport } = usePatternIO({
    instruments,
    setInstruments,
    grid,
    setGrid,
    timeSignature,
    setTimeSignature,
    noteDivision,
    setNoteDivision,
    loopCount,
    setLoopCount,
    tempo,
    setTempo,
    stepsPerBar,
    setStepsPerBar, // 新增，潮流樣式郵件
    isPlaying,
    setIsPlaying,
    setIsPaused,
    resetStep,
    stopPlayback,
    fileInputRef,
    onDrumsetUpdate,
    showDynamicMessage,
    t,
    isImportingRef,
    setIsImporting,
    lastDrumsetConfigRef,
    setSelectedPattern,
    setIsGridModified, // 新增，grid修改狀態追蹤
    drumsetConfig // 傳遞 drumsetConfig
  });

  // const responsive = useResponsive(); // 暫時不使用響應式

  // 多國語言的HIT_LABELS
  const HIT_LABELS: Record<string, string> = {
    x: t('hitTypes.normal'),
    X: t('hitTypes.accent'),
    g: t('hitTypes.ghost'),
    f: t('hitTypes.flam')
  };

  // 主題應用函數 - 加入錯誤處理
  const getThemeClass = (type: string): string => {
    try {
      return getClass(type) || '';
    } catch (error) {
      console.warn('Theme class error:', error);
      // 回退到基本樣式
      const fallback: Record<string, string> = {
        background: 'bg-white',
        text: 'text-gray-900',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        grid: 'bg-gray-100',
        border: 'border-gray-300',
        input: 'border-gray-300 bg-white text-gray-900',
        highlight: 'ring-red-400'
      };
      return fallback[type] || '';
    }
  };

  // 初始化樂器players
  useEffect(() => {
    const initializePlayers = async (): Promise<void> => {
      const newPlayers: Record<string, any> = {};
      
      for (const { id, sampleId } of instruments) {
        try {
          const audioUrl = getSafeAudioUrl(sampleId);
          const player = new Tone.Player(audioUrl).toDestination();
          newPlayers[id] = player;
          devLog('🎵 Player initialized for:', id, audioUrl);
        } catch (error) {
          console.warn(`Failed to initialize player for ${id}:`, error);
        }
      }
      
      setPlayers(newPlayers);
    };

    if (instruments?.length > 0) {
      initializePlayers();
    }
  }, [instruments]);

  // 更新拍號時重新計算每小節步數
  useEffect(() => {
    setStepsPerBar(calcStepsPerBar(timeSignature, noteDivision));
  }, [timeSignature, noteDivision]);

  // 將 Tone.js Transport 的拍號與本地狀態同步
  useEffect(() => {
    const { beats, base } = TIME_SIGNATURES[timeSignature];
    Tone.Transport.timeSignature = [beats, base];
    Tone.Transport.position = "0:0:0";
  }, [timeSignature]);

  // 載入音頻狀態管理 - 網格修改處理
  const handleGridModified = (): void => {
    setIsGridModified(true);
  };

  // Drumset Editor保存處理
  const handleDrumsetSave = (config: DrumsetConfig): void => {
    devLog('🛠️ Drumset saved:', config);
    if (onDrumsetUpdate) {
      onDrumsetUpdate(config);
    }
    showDynamicMessage(t('drumsetSaved') || 'Drumset saved!', 'success');
  };

  // Sound Management 保存處理
  const handleSoundManagementSave = (newInstruments: DrumInstrument[], kitName?: string): void => {
    const config: DrumsetConfig = {
      instruments: newInstruments,
      name: kitName
    };
    handleDrumsetSave(config);
    
    // 檢查iOS音頻狀態，如果音頻上下文被重置則更新狀態
    checkIOSAudioAfterAction('Sound Management save');
  };

  // 檢查iOS音頻狀態的通用函數
  const checkIOSAudioAfterAction = (actionName: string): void => {
    if (/iPhone|iPad|iPod/.test(navigator.userAgent) && iosAudioUnlocked) {
      setTimeout(() => {
        const isStillRunning = checkAudioContextState();
        if (!isStillRunning) {
          devLog(`⚠️ Audio context lost after ${actionName}, updating status`);
          setIosAudioUnlocked(false);
        }
      }, 1000); // 給一點時間讓音頻上下文穩定
    }
  };

  // 處理DrumsetEditor關閉
  const handleDrumsetEditorClose = (): void => {
    onCloseDrumsetEditor();
    checkIOSAudioAfterAction('Drumset Editor close');
  };

  // 當drumsetConfig改變時更新樂器列表
  useEffect(() => {
    if (!drumsetConfig || !drumsetConfig.instruments) return;

    // 防止相同配置的重複更新
    if (JSON.stringify(drumsetConfig) === JSON.stringify(lastDrumsetConfigRef.current)) {
      return;
    }

    // 如果正在導入，跳過drumsetConfig更新
    if (isImportingRef.current) {
      devLog('⏸️ Skipping drumset update during import');
      return;
    }

    lastDrumsetConfigRef.current = drumsetConfig;

    const instrumentsWithUrls = drumsetConfig.instruments.map((inst: DrumInstrument) => ({
      ...inst,
      url: getSafeAudioUrl(inst.sampleId)
    }));

    devLog('🛠️ Updating instruments from drumsetConfig:', instrumentsWithUrls.length);
    setInstruments(instrumentsWithUrls);

    // 更新grid以匹配新的樂器
    const newGrid: DrumGridData = {};
    instrumentsWithUrls.forEach(({ id }) => {
      newGrid[id] = grid[id] || Array(stepsPerBar).fill(null);
    });
    setGrid(newGrid);
  }, [drumsetConfig, stepsPerBar, grid]);

  // 當樂器改變時更新grid結構
  useEffect(() => {
    const newGrid: DrumGridData = {};
    instruments.forEach(({ id }) => {
      newGrid[id] = grid[id] || Array(stepsPerBar).fill(null);
    });
    setGrid(newGrid);
  }, [instruments, stepsPerBar]);

  // iOS音頻解鎖函數
  const unlockIOSAudio = async (): Promise<boolean> => {
    recordUnlockAttempt(); // 記錄解鎖嘗試
    
    if (!/iPhone|iPad|iPod/.test(navigator.userAgent)) {
      setIosAudioUnlocked(true);
      return true;
    }
    
    try {
      devLog('🍎 Starting iOS audio unlock process...');
      
      // 1. 創建並啟動AudioContext
      if (!iosAudioContextRef.current) {
        // 使用原生Web Audio API而不是Tone.js
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        iosAudioContextRef.current = new AudioContext();
      }
      
      const audioContext = iosAudioContextRef.current;
      
      // 2. 播放一個靜音音檔來解鎖
      const buffer = audioContext.createBuffer(1, 1, 22050);
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.start(0);
      
      devLog('🍎 Silent audio played, AudioContext state:', audioContext.state);
      
      // 3. 等待AudioContext變成running
      if (audioContext.state !== 'running') {
        await audioContext.resume();
      }
      
      // 4. 現在嘗試啟動Tone.js
      await Tone.start();
      devLog('🍎 Tone.js started, state:', Tone.context.state);
      
      // 5. 測試真實音檔
      const testUrl = '/samples/kick-classic.wav';
      devLog('🍎 Testing real audio file:', testUrl);
      
      // 使用HTML5 Audio進行測試
      const testAudio = new Audio(testUrl);
      testAudio.volume = 0.3;
      
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Audio load timeout'));
        }, 5000);
        
        testAudio.oncanplaythrough = () => {
          clearTimeout(timeout);
          testAudio.play().then(() => {
            devLog('🍎 HTML5 Audio test successful');
            resolve();
          }).catch(reject);
        };
        
        testAudio.onerror = (e) => {
          clearTimeout(timeout);
          reject(new Error(`Audio load error: ${e}`));
        };
        
        testAudio.load();
      });
      
      setIosAudioUnlocked(true);
      showDynamicMessage(t('iosAudioUnlockSuccess') || '✅ iOS audio unlock successful!', 'success');
      devLog('🍎 iOS audio unlock successful!');
      return true;
      
    } catch (error) {
      console.error('🍎 iOS audio unlock failed:', error);
      recordUnlockAttempt(); // 記錄失敗的嘗試
      showDynamicMessage(t('iosAudioUnlockFailed') || '❌ iOS Audio Unlock Failed', 'error');
      return false;
    }
  };

  // Demo functions
  const startDemo = async (): Promise<void> => {
    devLog('🎭 Demo mode: Starting demo process...');

    setDemoStatus('loading');

    // Stop existing playback only if something is playing or paused
    if (isPlaying || isPaused) {
      stopPlayback();
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    try {
      // 先隨機選擇一個preset pattern
      const allPatterns = getAllDrumPatterns();
      const randomIndex = Math.floor(Math.random() * allPatterns.length);
      const randomPattern = allPatterns[randomIndex];

      devLog(`🎭 Demo mode: Loading pattern ${randomPattern.key}`);
      setIsDemoMode(true);
      
      // 載入選擇的pattern並同步selectedPattern狀態
      await loadPresetPattern(randomPattern.key);
      
      // iPhone簡化版：只載入pattern，讓用戶手動播放，使用動態消息替代alert
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        devLog('🍎 Demo mode: iPhone detected - pattern loaded, user can press Play');
        setIsDemoMode(false); // iPhone上Demo只負責載入，完成後回到正常狀態
        setDemoStatus('idle');

        // 使用動態消息替代alert
        showDynamicMessage(
          `${t('demoLoadComplete')} ${t(randomPattern.nameKey)} (${randomPattern.tempo} BPM)\n\n${t('pressPlayToStart')}`,
          'success',
          5000
        );
        return;
      }
      
      // 非iPhone設備：繼續原有的自動播放邏輯
      devLog('🎭 Demo mode: Non-iOS device, proceeding with auto-play...');
      
      // 等待一下讓pattern載入完成
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 現在使用與togglePlayback相同的邏輯啟動播放
      // 先嘗試啟動Tone.js AudioContext（對iOS很重要）
      if (Tone.context.state !== 'running') {
        devLog('🎭 Demo mode: AudioContext not running, starting...');
        await Tone.start();
        devLog('🎭 Demo mode: AudioContext started successfully');
      }
      
      // 檢查音檔是否載入完成
      const loadingPromises: Promise<void>[] = [];
      
      instruments.forEach(({ id }) => {
        const player = players[id];
        if (player && !player.buffer?.loaded) {
          devLog(`🎵 Demo mode: Player ${id} not loaded, waiting...`);
          
          // 創建載入Promise
          const loadPromise = new Promise<void>((resolve) => {
            const timeout = setTimeout(() => {
              console.warn(`⏰ Demo mode: Player ${id} load timeout`);
              resolve(); // 不要因為超時而阻止播放
            }, 3000);
            
            const checkLoaded = (): void => {
              if (player.buffer?.loaded) {
                clearTimeout(timeout);
                devLog(`✅ Demo mode: Player ${id} loaded successfully`);
                resolve();
              } else {
                setTimeout(checkLoaded, 100);
              }
            };
            
            checkLoaded();
          });
          
          loadingPromises.push(loadPromise);
        }
      });
      
      // 如果有音檔還在載入，等待它們（但有超時限制）
      if (loadingPromises.length > 0) {
        devLog(`⏳ Demo mode: Waiting for ${loadingPromises.length} audio files to load...`);
        try {
          await Promise.race([
            Promise.all(loadingPromises),
            new Promise(resolve => setTimeout(resolve, 5000)) // 5秒超時
          ]);
          devLog('✅ Demo mode: Audio files loaded (or timeout reached)');
        } catch (error) {
          console.warn('⚠️ Demo mode: Some audio files failed to load:', error);
        }
      }
      
      // 開始播放
      setIsPlaying(true);
      setDemoStatus('playing');
      if (isPaused) {
        devLog('🎭 Demo mode: Resuming from pause');
        setIsPaused(false);
      } else {
        devLog('🎭 Demo mode: Starting fresh');
        // 從頭開始時重置位置
        resetStep();
      }
      
      devLog(`🎭 Demo mode: Playing ${randomPattern.key}`);
      
    } catch (error) {
      console.error('Demo mode error:', error);
      stopPlayback();
      setIsDemoMode(false);
      setDemoStatus('idle');
      
      // 根據設備給出不同的錯誤提示，使用動態消息
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        showDynamicMessage(
          `${t('demoLoadFailed')}: ${(error as Error).message}\n\n${t('tryAgainOrSelectManually')}`,
          'error',
          5000
        );
      } else {
        showDynamicMessage(`${t('demoPlayError')}: ${(error as Error).message}`, 'error');
      }
    }
  };

  const stopDemo = (): void => {
    setIsDemoMode(false);
    // Only stop playback if something is playing or paused
    setDemoStatus('stopping');
    if (isPlaying || isPaused) {
      stopPlayback();
    }
    setDemoStatus('idle');
  };

  // Reset pattern function
  const resetPattern = (): void => {
    if (window.confirm(t('confirmResetPattern') || 'Are you sure you want to clear all drum patterns?')) {
      devLog('🚨 SETGRID: Reset pattern operation');
      
      // 如果正在Demo模式，先停止Demo
      if (isDemoMode) {
        stopDemo();
      }
      
      // 重置步數到基礎值（這會觸發DrumRNNButton重置倍數為1x）
      const baseSteps = calcStepsPerBar(timeSignature, noteDivision);
      setStepsPerBar(baseSteps);
      console.log(`🔄 Pattern Reset: Reset stepsPerBar to base value ${baseSteps}`);
      
      setGrid(() => {
        const newGrid: DrumGridData = {};
        instruments.forEach(({ id }) => {
          newGrid[id] = Array(baseSteps).fill(null);
        });
        return newGrid;
      });
      
      // 重設選擇器和grid修改狀態
      setSelectedPattern("");
      setIsGridModified(false);
      
      // 重置所有時間設定回預設值
      setGrooveType('normal');
      setShuffleAmount(0.5);
      setSwingRatio(0.8);
      
      // 停止播放並重新套用當前設定
      stopPlayback();
      
      devLog('Pattern reset');
    }
  };

  // ASCII 匯入處理函數
  const handleAsciiImport = ({ grid: importedGrid, drumKitId, settings }: any): void => {
    // 標記正在importing狀態
    setIsImporting(true);
    
    // 準備更新後的樂器
    let updatedInstruments = [...instruments];
    
    if (drumKitId) {
      const kit = getDrumKit(drumKitId);
      if (kit && kit.instruments) {
        updatedInstruments = kit.instruments.map((inst: any) => ({
          ...inst,
          url: getSafeAudioUrl(inst.sampleId)
        }));
      }
    }
    
    // 檢查是否有新ID需要更新
    const importedIds = Object.keys(importedGrid);
    const currentIds = updatedInstruments.map(inst => inst.id);
    const missingIds = importedIds.filter(id => !currentIds.includes(id));
    
    if (missingIds.length > 0) {
      devLog(`🔍 ASCII Import: Found new instrument IDs: ${missingIds.join(', ')}`);
      
      // 檢查是否有在ID中包含樣本sample
      const newInstruments = missingIds.map((id, index) => {
        // 檢查是否有針對這個ID的現有配置sample
        const instrumentConfig = getInstrumentConfigById(id, process.env.NODE_ENV === 'development');
        return {
          id,
          name: instrumentConfig.name,
          type: instrumentConfig.type,
          sampleId: instrumentConfig.sampleId,
          url: getSafeAudioUrl(instrumentConfig.sampleId),
          color: instrumentConfig.color
        };
      });
      
             // 重組樂器陣列，確保順序與匯入的grid一致
       // 優先保留現有樂器的配置，然後再加入新的樂器來填補缺口
       updatedInstruments = importedIds.map(id => {
         const existingInstrument = updatedInstruments.find(inst => inst.id === id);
         if (existingInstrument) {
           // 保留現有樂器的配置不變
           devLog(`🔄 [Keep] Preserving existing instrument ${id}: ${existingInstrument.name} (${existingInstrument.type})`);
           return existingInstrument;
         } else {
           // 建立新樂器來配對
           const newInstrument = newInstruments.find(inst => inst.id === id);
           if (!newInstrument) {
             throw new Error(`Failed to create instrument config for ID: ${id}`);
           }
           devLog(`🆕 [New] Adding new instrument ${id}: ${newInstrument.name} (${newInstrument.type})`);
           return newInstrument;
         }
       }).filter(Boolean) as DrumInstrument[];
      
      devLog(`🔍 ASCII Import: Updated instruments to match imported IDs`);
    } else {
      // 現有所有樂器ID都存在，保持原本的樂器陣列，但調整順序
      updatedInstruments = updatedInstruments
        .filter(inst => importedIds.includes(inst.id))
        .map(inst => {
          // 保留現有樂器的配置不變
          devLog(`🔄 [Keep] Preserving existing instrument ${inst.id}: ${inst.name} (${inst.type})`);
          return inst;
        });
      // 重排ID順序
      updatedInstruments.sort((a, b) => importedIds.indexOf(a.id) - importedIds.indexOf(b.id));
    }
    
    // 更新樂器
    setInstruments(updatedInstruments);
    localStorage.setItem('drumsetConfig', JSON.stringify({ instruments: updatedInstruments }));
    if (onDrumsetUpdate) {
      onDrumsetUpdate({ instruments: updatedInstruments });
    }
    
    // 如果有設定，更新設定
    if (settings) {
      if (settings.timeSignature) {
        devLog(`🔍 ASCII Import: Setting time signature to ${settings.timeSignature}`);
        setTimeSignature(settings.timeSignature);
      }
      if (settings.noteDivision) {
        devLog(`🔍 ASCII Import: Setting note division to ${settings.noteDivision}`);
        setNoteDivision(settings.noteDivision);
      }
      if (settings.tempo) {
        devLog(`🔍 ASCII Import: Setting tempo to ${settings.tempo}`);
        setTempo(settings.tempo);
      }
    }
    
    // 根據匯入的長度決定是否需要調整stepsPerBar設定
    const importedLengths = Object.values(importedGrid).map((pattern: any) => pattern ? pattern.length : 0);
    if (importedLengths.length > 0) {
      const maxLength = Math.max(...importedLengths);
      if (maxLength > 0 && maxLength !== stepsPerBar) {
        devLog(`🔍 ASCII Import: Adjusting stepsPerBar from ${stepsPerBar} to ${maxLength}`);
        setStepsPerBar(maxLength);
        // 直接匯入長度正確的grid，不需要fixGridLength修正
        setGrid(importedGrid);
      } else {
        setGrid(fixGridLength(importedGrid, stepsPerBar));
      }
    } else {
      setGrid(fixGridLength(importedGrid, stepsPerBar));
    }
    
    // 延遲清除importing狀態，讓grid能正確更新
    setTimeout(() => {
      setIsImporting(false);
      setIsGridModified(false); // 重設grid修改狀態
    }, 500);
    
    showDynamicMessage(t('asciiImportSuccess') || 'ASCII pattern imported successfully!', 'success');
  };

  return (
    <div className={`p-4 max-w-full mx-auto space-y-6 ${getThemeClass('background')} ${getThemeClass('text')}`}>
      {/* iOS音頻狀態提醒 */}
      {shouldShowUnlockReminder && (
        <div className="fixed top-4 right-4 z-40 max-w-sm bg-orange-50 border-l-4 border-orange-400 text-orange-700 p-4 rounded-lg shadow-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span className="text-lg">🔄</span>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="font-medium">{t('iosAudioSessionReset')}</h3>
              <p className="text-sm mt-1">
                {userHasEverUnlocked 
                  ? t('iosAudioSessionResetMessage') 
                  : t('iosAudioFirstTimeMessage')
                }
              </p>
              <button
                onClick={unlockIOSAudio}
                className="mt-2 px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded text-sm font-medium transition-colors"
              >
                🔓 {t('unlockAudioSystem')}
              </button>
            </div>
            <div className="flex-shrink-0 ml-2">
              <button
                onClick={() => setIosAudioUnlocked(true)} // 暫時隱藏提醒
                className="text-orange-400 hover:text-orange-600 text-sm"
                title={t('close')}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 動態訊息顯示 */}
      {dynamicMessage && (
        <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4 p-4 rounded-lg shadow-lg border-l-4 ${
          dynamicMessage.type === 'success' 
            ? 'bg-green-50 border-green-400 text-green-800' 
            : dynamicMessage.type === 'error'
            ? 'bg-red-50 border-red-400 text-red-800'
            : 'bg-blue-50 border-blue-400 text-blue-800'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {dynamicMessage.type === 'success' && <span className="text-lg">✅</span>}
              {dynamicMessage.type === 'error' && <span className="text-lg">❌</span>}
              {dynamicMessage.type === 'info' && <span className="text-lg">ℹ️</span>}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium whitespace-pre-line">
                {dynamicMessage.message}
              </p>
            </div>
            <div className="flex-shrink-0 ml-4">
              <button
                onClick={() => setDynamicMessage(null)}
                className="inline-flex text-sm"
              >
                <span className="sr-only">{t('close')}</span>
                ❌
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for imports */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.txt"
        onChange={importPattern}
        style={{ display: 'none' }}
      />

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-2">🥁 {t('title') || 'ASCII Drum Editor'}</h1>
          <p className="text-sm md:text-lg opacity-75">{t('appDescription') || 'Create and edit drum patterns with ASCII notation'}</p>
        </div>

        {/* Main Controls */}
        <PlaybackControls
          iosAudioUnlocked={iosAudioUnlocked}
          unlockIOSAudio={unlockIOSAudio}
          showDynamicMessage={showDynamicMessage}
          instruments={instruments}
          players={players}
          togglePlayback={togglePlayback}
          stopPlayback={stopPlayback}
          isPlaying={isPlaying}
          isPaused={isPaused}
          isDemoMode={isDemoMode}
          demoStatus={demoStatus}
          startDemo={startDemo}
          stopDemo={stopDemo}
          onToggleDrumsetEditor={onToggleDrumsetEditor}
          showDrumsetEditor={showDrumsetEditor}
          exportPattern={exportPattern}
          triggerImport={triggerImport}
          resetPattern={resetPattern}
          selectedPattern={selectedPattern}
          setSelectedPattern={setSelectedPattern}
          loadPresetPattern={loadPresetPattern}
          getAllDrumPatterns={getAllDrumPatterns}
          fileInputRef={fileInputRef}
          importPattern={importPattern}
          timeSignature={timeSignature}
          setTimeSignature={setTimeSignature}
          noteDivision={noteDivision}
          setNoteDivision={setNoteDivision}
          loopCount={loopCount}
          setLoopCount={setLoopCount}
          tempo={tempo}
          setTempo={setTempo}
          TEMPO_MIN={TEMPO_MIN}
          TEMPO_MAX={TEMPO_MAX}
          validateTempo={validateTempo}
          advancedMode={advancedMode}
          setAdvancedMode={setAdvancedMode}
          isAudioLoading={isAudioLoading}
          debugMode={debugMode}
          setDebugMode={setDebugMode}
          grooveType={grooveType}
          setGrooveType={setGrooveType}
          shuffleAmount={shuffleAmount}
          setShuffleAmount={setShuffleAmount}
          swingRatio={swingRatio}
          setSwingRatio={setSwingRatio}
        />

        {/* Drum Grid */}
        <DrumGrid
          instruments={instruments}
          grid={grid}
          setGrid={setGrid}
          stepsPerBar={stepsPerBar}
          setStepsPerBar={setStepsPerBar}
          timeSignature={timeSignature}
          noteDivision={noteDivision}
          isPlaying={isPlaying}
          isPaused={isPaused}
          step={step}
          advancedMode={advancedMode}
          HIT_LABELS={HIT_LABELS}
          t={t}
          exportPattern={exportPattern}
          triggerImport={triggerImport}
          resetPattern={resetPattern}
          onGridModified={handleGridModified}
        />

        {/* DrumRNN Panel */}
        <DrumRNNPanel
          instruments={instruments}
          grid={grid}
          setGrid={setGrid}
          stepsPerBar={stepsPerBar}
          setStepsPerBar={setStepsPerBar}
          timeSignature={timeSignature}
          noteDivision={noteDivision}
          selectedPattern={selectedPattern}
          onGridModified={handleGridModified}
          isGridModified={isGridModified}
          temperature={temperature}
          setTemperature={setTemperature}
        />

        {/* DrumsetEditor Panel */}
        <DrumsetEditorPanel
          onToggleDrumsetEditor={onToggleDrumsetEditor}
          showDrumsetEditor={showDrumsetEditor}
          onCloseDrumsetEditor={handleDrumsetEditorClose}
          onDrumsetSave={handleDrumsetSave}
          onSoundManagementSave={handleSoundManagementSave}
          drumsetConfig={drumsetConfig}
          showDynamicMessage={showDynamicMessage}
          debugMode={debugMode}
          currentInstruments={instruments}
        />

        {/* ASCII Output */}
        <div className="mt-4">
          <AsciiOutput
            instruments={instruments}
            grid={grid}
            stepsPerBar={stepsPerBar}
            timeSignature={timeSignature}
            noteDivision={noteDivision}
            tempo={tempo}
            onImport={handleAsciiImport}
            currentInstruments={instruments}
            disabled={isImporting}
            onError={(msg: string) => showDynamicMessage(msg, 'error')}
          />
        </div>

        {/* Help Section - 幫助區塊 */}
        <HelpSection />
      </div>
    </div>
  );
} 